import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { getApiUrl, API_CONFIG } from "../config/api";
import ReviewCard from "./ReviewCard";
import { useReviewAnalysis } from "../hooks/useReviewAnalysis";
import { useReviewResponse } from "../hooks/useReviewResponse";
import { Review } from "../types";
import { useParams } from "react-router-dom";
import {
  useWindowVirtualizer,
  measureElement as rvMeasureElement,
} from "@tanstack/react-virtual";

interface ProductReviewsProps {
  productId?: number;
  offer_id?: string;
  sku?: number;
  platform?: string;
  productName: string;
}

const ProductReviews: React.FC<ProductReviewsProps> = ({
  productId,
  offer_id,
  sku: skuProp,
  platform: platformProp,
}) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState<number>(1);
  const [totalReviews, setTotalReviews] = useState<number | null>(null);
  const lastIdRef = useRef<string | null>(null);
  const wbSkipRef = useRef<number>(0);
  const [sku, setSku] = useState<number | null>(skuProp ?? null);
  useEffect(() => {
    if (typeof skuProp === "number") {
      setSku(skuProp);
    }
  }, [skuProp]);
  // Переключатель "Только с текстом" как на странице Отзывы
  const [onlyWithText, setOnlyWithText] = useState<boolean>(() => {
    const saved = localStorage.getItem("onlyWithText");
    return saved !== null ? JSON.parse(saved) : false;
  });
  const handleOnlyWithTextChange = useCallback((value: boolean) => {
    setOnlyWithText(value);
    localStorage.setItem("onlyWithText", JSON.stringify(value));
  }, []);
  const fetchReviewsRef = useRef<
    ((pageParam: number, isLoadMore?: boolean) => Promise<void>) | null
  >(null);
  const {
    platform: platformParam,
    idType: idTypeParam,
    storeId,
    productId: routeProductId,
  } = useParams<{
    platform?: string;
    idType?: string;
    storeId?: string;
    productId?: string;
  }>();

  // Виртуализация списка с прокруткой окна (учитываем фильтр только с текстом)
  const rows = React.useMemo(
    () =>
      onlyWithText
        ? reviews.filter((r) => r.text && r.text.trim().length > 0)
        : reviews,
    [reviews, onlyWithText]
  );
  const virtualizer = useWindowVirtualizer({
    count: rows.length + (hasMore ? 1 : 0),
    estimateSize: () => 220,
    overscan: 10,
    measureElement: rvMeasureElement,
    getItemKey: (index) => rows[index]?.id ?? index,
  });
  const virtualItems = virtualizer.getVirtualItems();
  const lastVirtualItem = virtualItems[virtualItems.length - 1];
  const lastIndex = lastVirtualItem ? lastVirtualItem.index : -1;
  const prevLastIndexRef = useRef<number | null>(null);
  const isFetchingMoreRef = useRef<boolean>(false);
  const didInitialLoadRef = useRef<boolean>(false);
  const requestIdRef = useRef<number>(0);

  // При смене режима отображения (только с текстом/все) сбрасываем курсор автодогрузки
  useEffect(() => {
    prevLastIndexRef.current = null;
  }, [onlyWithText]);

  // Добавляем хуки для анализа и генерации ответов
  const { analyzingReviews, handleAnalyzeReview } = useReviewAnalysis();
  const {
    generatingResponses,
    handleGenerateResponse,
    handleRegenerateResponse,
  } = useReviewResponse();

  // Создаем обертки для обработчиков, которые обновляют локальное состояние
  const handleAnalyzeReviewWrapper = async (
    reviewId: string,
    platform: "ozon" | "wildberries" | "yandex"
  ): Promise<boolean> => {
    // Создаем адаптер для преобразования типов
    const setReviewsAdapter = (updater: React.SetStateAction<any[]>) => {
      setReviews((prevReviews) => {
        const newReviews =
          typeof updater === "function" ? updater(prevReviews) : updater;
        // Преобразуем обратно в OzonReview формат, сохраняя оригинальные поля
        return newReviews;
      });
    };
    const ok = await handleAnalyzeReview(reviewId, setReviewsAdapter, platform);
    return ok;
  };

  const handleGenerateResponseWrapper = async (reviewId: string) => {
    const setReviewsAdapter = (updater: React.SetStateAction<any[]>) => {
      setReviews((prevReviews) => {
        const newReviews =
          typeof updater === "function" ? updater(prevReviews) : updater;
        return newReviews;
      });
    };
    await handleGenerateResponse(reviewId, setReviewsAdapter);
  };

  const handleRegenerateResponseWrapper = async (reviewId: string) => {
    const setReviewsAdapter = (updater: React.SetStateAction<any[]>) => {
      setReviews((prevReviews) => {
        const newReviews =
          typeof updater === "function" ? updater(prevReviews) : updater;
        return newReviews;
      });
    };
    await handleRegenerateResponse(reviewId, setReviewsAdapter);
  };

  const fetchReviews = useCallback(
    async (pageParam: number = 1, isLoadMore: boolean = false) => {
      if (!productId && !offer_id && !(storeId && routeProductId)) return;

      try {
        const currentRequestId = ++requestIdRef.current;
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("token");
        const headers = {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        };

        const params = new URLSearchParams({
          limit: "20",
          page: String(pageParam),
        });

        // Для страницы товара по маршруту со storeId и productId —
        // выбираем логику по платформе (OZON DB или Wildberries API с nmId)
        let url: string;
        if (storeId && routeProductId) {
          const resolvedPlatform = (
            platformProp ||
            platformParam ||
            "ozon"
          ).toLowerCase();
          if (resolvedPlatform === "ozon") {
            // Используем sku, переданный сверху из информации о товаре
            const dbParams = new URLSearchParams();
            if (sku != null) {
              dbParams.append("sku", String(sku));
            } else {
              // SKU ещё не доступен — не показываем ошибку и не делаем запрос
              setLoading(false);
              return;
            }
            dbParams.append("limit", "20");
            if (isLoadMore && lastIdRef.current) {
              dbParams.append("lastId", lastIdRef.current);
            }
            url = `${getApiUrl(
              API_CONFIG.ENDPOINTS.OZON_PRODUCT_REVIEWS_DB
            )}?${dbParams}`;
          } else if (resolvedPlatform === "wildberries") {
            // Используем тот же эндпоинт/схему, что и страница "Отзывы", но фильтруем по конкретному nmId товара
            const wbParams = new URLSearchParams();
            const take = 20;
            const skip = isLoadMore ? wbSkipRef.current : 0;
            wbParams.append("take", String(take));
            wbParams.append("skip", String(skip));
            wbParams.append("order", "dateDesc");
            wbParams.append("storeId", storeId);
            // nmId — это идентификатор товара WB из маршрута
            const nmIdNum = Number(routeProductId);
            if (Number.isFinite(nmIdNum)) {
              wbParams.append("nmId", String(nmIdNum));
            }
            url = `${getApiUrl(API_CONFIG.ENDPOINTS.WILDBERRIES_REVIEWS)}?${wbParams}`;
          } else if (resolvedPlatform === "yandex") {
            // Яндекс: фильтруем по modelId (берём из mappings), если доступен. Пока прокидываем productId как modelId — сервер должен ожидать modelId
            const yaParams = new URLSearchParams();
            yaParams.append("storeId", storeId);
            // productId в нашем роуте для Яндекса = offerId; для корректной фильтрации отзывов API требует modelId.
            // На этом шаге передадим его в параметре modelId, если равен числу.
            const maybeModelId = Number(routeProductId);
            if (Number.isFinite(maybeModelId)) {
              yaParams.append("modelId", String(maybeModelId));
            }
            // Пагинация допустима вместе с modelId
            if (lastIdRef.current) {
              yaParams.append("page_token", lastIdRef.current);
            }
            yaParams.append("limit", "21");
            url = `${getApiUrl(API_CONFIG.ENDPOINTS.YANDEX_REVIEWS)}?${yaParams.toString()}`;
          } else {
            // для других платформ остаётся универсальная ручка
            const endpoint = API_CONFIG.ENDPOINTS.UNIVERSAL_PRODUCT_REVIEWS;
            url = `${getApiUrl(endpoint)}?${params}`;
          }
        } else {
          const endpoint = API_CONFIG.ENDPOINTS.UNIVERSAL_PRODUCT_REVIEWS;
          url = `${getApiUrl(endpoint)}?${params}`;
        }

        const response = await fetch(url, {
          headers,
        });

        if (response.ok) {
          const data = await response.json();
          // Если за время запроса пришел новый запрос — игнорируем устаревший ответ
          if (currentRequestId !== requestIdRef.current) {
            return;
          }
          // Ветвим обработку ответа под разные источники
          let unifiedHasMore: boolean = false;
          let unifiedReviews: any[] = [];
          const isWbStoreRoute = Boolean(
            storeId &&
              routeProductId &&
              (platformProp || platformParam || "ozon").toLowerCase() ===
                "wildberries"
          );
          const isYaStoreRoute = Boolean(
            storeId &&
              routeProductId &&
              (platformProp || platformParam || "ozon").toLowerCase() ===
                "yandex"
          );
          if (isWbStoreRoute) {
            const feedbacks =
              data && data.data && Array.isArray(data.data.feedbacks)
                ? data.data.feedbacks
                : [];
            unifiedHasMore = feedbacks.length >= 20;
            // Маппинг в общий формат (как на странице "Отзывы")
            unifiedReviews = feedbacks.map((wb: any) => {
              const textParts = [wb.text, wb.pros, wb.cons].filter(Boolean);
              const productName = wb.productDetails?.productName || "";
              const productOfferId =
                (wb.productDetails?.supplierArticle as any)?.toString?.() || "";
              const productIdStr =
                (wb.productDetails?.nmId as any)?.toString?.() || "";
              return {
                comments_amount: 0,
                id: wb.id,
                isAnalyzed: Boolean(wb.isAnalyzed) || false,
                isAnsweredBySeller: Boolean(wb.answer?.text),
                isFallback: Boolean(wb.isFallback) || false,
                isResponded: Boolean(wb.answer?.text),
                is_rating_participant: false,
                offer_id: productOfferId,
                order_status: wb.state || "",
                photos_amount: Array.isArray(wb.photoLinks)
                  ? wb.photoLinks.length
                  : 0,
                platform: "wildberries",
                problems: wb.problems ?? null,
                product: {
                  created_at: "",
                  currency_code: "",
                  id: productIdStr,
                  images: [],
                  is_archived: false,
                  is_autoarchived: false,
                  name: productName,
                  offer_id: productOfferId,
                  price: 0,
                  primary_image: [],
                  sku: productIdStr,
                  statuses: {
                    is_created: false,
                    moderate_status: "",
                    status: "",
                    status_description: "",
                    status_failed: "",
                    status_name: "",
                    status_tooltip: "",
                    status_updated_at: "",
                    validation_status: "",
                  },
                  stocks: { has_stock: false, stocks: [] },
                  updated_at: "",
                  visibility_details: { has_price: false, has_stock: false },
                },
                published_at: wb.createdDate,
                rating: wb.productValuation ?? 0,
                recommendations: wb.recommendations ?? null,
                responseTemplate: wb.responseTemplate ?? null,
                sellerAnswer: wb.answer?.text || null,
                sentiment: wb.sentiment ?? null,
                sentimentAnalysis: wb.sentimentAnalysis ?? null,
                sku: Number(wb.productDetails?.nmId) || 0,
                status: wb.state || "",
                text: textParts.join(" \n"),
                videos_amount: wb.video ? 1 : 0,
                storeId: (wb as any).storeId,
                storeName: (wb as any).storeName,
              } as Review;
            });
            // Обновляем skip для следующей страницы
            wbSkipRef.current += unifiedReviews.length;
          } else if (isYaStoreRoute) {
            const list: any[] = Array.isArray(data?.reviews)
              ? (data.reviews as any[])
              : [];
            // Маппинг Yandex-отзывов в общий формат Review, как на странице "Отзывы"
            unifiedReviews = list.map((ya: any) => {
              const textParts: string[] = [];
              if (ya?.description?.advantages)
                textParts.push(String(ya.description.advantages));
              if (ya?.description?.disadvantages)
                textParts.push(String(ya.description.disadvantages));
              if (ya?.description?.comment)
                textParts.push(String(ya.description.comment));
              const photosCount = Array.isArray(ya?.media?.photos)
                ? ya.media.photos.length
                : 0;
              const videosCount = Array.isArray(ya?.media?.videos)
                ? ya.media.videos.length
                : 0;
              const sellerAnswerText =
                ya?.answer !== undefined && ya?.answer !== null
                  ? ya.answer
                  : (ya?.sellerAnswer ?? null);
              const isAnswered =
                typeof sellerAnswerText === "string"
                  ? sellerAnswerText.trim().length > 0
                  : Boolean(sellerAnswerText);
              const productName = ya?.product?.name || "";
              const productIdStr = String(routeProductId || "");
              const reviewObj: Review = {
                // Обязательные поля Review
                comments_amount: Number(ya?.statistics?.commentsCount || 0),
                id: String(ya?.feedbackId ?? ya?.id ?? ""),
                isAnalyzed: Boolean(ya?.isAnalyzed) || false,
                isAnsweredBySeller:
                  (ya as any).isAnsweredBySeller ?? isAnswered,
                isFallback: Boolean(ya?.isFallback) || false,
                isResponded:
                  (ya as any).isResponded ?? (isAnswered ? true : false),
                is_rating_participant: false,
                offer_id: productIdStr,
                order_status: "",
                photos_amount: photosCount,
                platform: "yandex",
                product: {
                  created_at: "",
                  currency_code: "",
                  id: productIdStr,
                  images: [],
                  is_archived: false,
                  is_autoarchived: false,
                  name: productName,
                  offer_id: productIdStr,
                  price: 0,
                  primary_image: [],
                  sku: productIdStr,
                  statuses: {
                    is_created: false,
                    moderate_status: "",
                    status: "",
                    status_description: "",
                    status_failed: "",
                    status_name: "",
                    status_tooltip: "",
                    status_updated_at: "",
                    validation_status: "",
                  },
                  stocks: { has_stock: false, stocks: [] },
                  updated_at: "",
                  visibility_details: { has_price: false, has_stock: false },
                },
                published_at: String(ya?.createdAt || ""),
                rating: Number(ya?.statistics?.rating ?? 0),
                recommendations: ya?.recommendations ?? null,
                responseTemplate: ya?.responseTemplate ?? null,
                sellerAnswer: (sellerAnswerText as any) ?? null,
                sentiment: ya?.sentiment ?? null,
                sentimentAnalysis: ya?.sentimentAnalysis ?? null,
                sku: 0,
                status: "",
                text: textParts.join(" \n").trim(),
                videos_amount: videosCount,
                // Доп. данные для карточки (не мешают типу)
                ...({
                  author:
                    (ya && (ya.author || ya.authorName || ya.userName)) || "",
                  yandex: {
                    advantages:
                      (ya?.description?.advantages as string | undefined) || "",
                    disadvantages:
                      (ya?.description?.disadvantages as string | undefined) ||
                      "",
                    comment:
                      (ya?.description?.comment as string | undefined) || "",
                    author:
                      (ya && (ya.author || ya.authorName || ya.userName)) || "",
                  },
                } as any),
                storeId: storeId,
                storeName: undefined,
              };
              return reviewObj;
            });
            const nextToken = data?.result?.paging?.nextPageToken as
              | string
              | undefined;
            unifiedHasMore = Boolean(nextToken);
            lastIdRef.current = nextToken || null;
          } else {
            // Унифицируем стандартный ответ has_more/has_next и last_id/lastId
            unifiedHasMore =
              typeof data.hasMore === "boolean"
                ? data.hasMore
                : Boolean(data.has_next);
            unifiedReviews = Array.isArray(data.reviews) ? data.reviews : [];
          }
          if (unifiedReviews && Array.isArray(unifiedReviews)) {
            if (isLoadMore) {
              setReviews((prev) => [...prev, ...unifiedReviews]);
            } else {
              setReviews(unifiedReviews);
            }
            setHasMore(Boolean(unifiedHasMore));
            setPage(pageParam);
            // Для Yandex курсор хранится отдельно (nextPageToken). Не затираем его lastId из других форматов.
            const isYaStoreRoute = Boolean(
              storeId &&
                routeProductId &&
                (platformProp || platformParam || "ozon").toLowerCase() ===
                  "yandex"
            );
            if (!isYaStoreRoute) {
              const respLastId =
                (data && (data.last_id || data.lastId)) || null;
              lastIdRef.current = respLastId;
            }
            if (typeof data.totalReviews === "number") {
              setTotalReviews(data.totalReviews);
            }
          } else {
            setReviews([]);
            setHasMore(false);
          }
        } else {
          const errorData = await response.json();
          if (currentRequestId !== requestIdRef.current) {
            return;
          }
          setError(errorData.message || "Ошибка при загрузке отзывов");
        }
      } catch (err) {
        // Игнорируем ошибку, если запрос устарел
        if (requestIdRef.current === 0) return;
        const errorMessage =
          err instanceof Error ? err.message : "Неизвестная ошибка";
        setError(errorMessage);
        console.error("Ошибка при загрузке отзывов:", err);
      } finally {
        setLoading(false);
        isFetchingMoreRef.current = false;
      }
    },
    [
      productId,
      offer_id,
      platformProp,
      platformParam,
      storeId,
      routeProductId,
      sku,
    ]
  );

  useEffect(() => {
    fetchReviewsRef.current = fetchReviews;
  }, [fetchReviews]);

  useEffect(() => {
    // Сброс при смене товара/платформы/типа ID
    setReviews([]);
    setHasMore(true);
    setPage(1);
    didInitialLoadRef.current = false;
    lastIdRef.current = null;
    wbSkipRef.current = 0;
  }, [
    productId,
    offer_id,
    platformParam,
    idTypeParam,
    user?.ozonApiKey,
    storeId,
    routeProductId,
  ]);

  // Первичная загрузка после того, как все необходимые параметры готовы (для OZON нужен sku)
  useEffect(() => {
    if (didInitialLoadRef.current) return;
    const resolvedPlatform = (
      platformProp ||
      platformParam ||
      "ozon"
    ).toLowerCase();
    const canLoad =
      (storeId &&
        routeProductId &&
        ((resolvedPlatform === "ozon" && sku != null) ||
          resolvedPlatform === "wildberries" ||
          resolvedPlatform === "yandex")) ||
      (!storeId && !routeProductId); // универсальный путь
    if (!canLoad) return;
    didInitialLoadRef.current = true;
    fetchReviewsRef.current?.(1, false);
  }, [platformProp, platformParam, storeId, routeProductId, sku]);

  // Автодогрузка при достижении низа (как на странице "Отзывы")
  useEffect(() => {
    if (lastIndex === -1) return;
    if (prevLastIndexRef.current === null) {
      prevLastIndexRef.current = lastIndex;
      return;
    }
    // Триггерим догрузку, когда виден индикатор (виртуальный индекс >= rows.length)
    const hasReachedEnd = lastIndex >= rows.length - 1;
    const scrolledFurther = lastIndex > prevLastIndexRef.current;
    // Для OZON-режима (БД) требуется lastId для следующей страницы
    const resolvedPlatform = (
      platformProp ||
      platformParam ||
      "ozon"
    ).toLowerCase();
    const isOzonDbMode = Boolean(
      storeId && routeProductId && resolvedPlatform === "ozon"
    );
    const isYandexMode = Boolean(
      storeId && routeProductId && resolvedPlatform === "yandex"
    );
    const paginationReady = isOzonDbMode
      ? !!lastIdRef.current
      : isYandexMode
        ? !!lastIdRef.current
        : true;
    const canLoadMore =
      hasMore &&
      hasReachedEnd &&
      // Для первой загрузки следующей страницы для Yandex допускаем равенство (без строгого >)
      (isYandexMode
        ? lastIndex >= (prevLastIndexRef.current ?? -1)
        : scrolledFurther) &&
      paginationReady &&
      !loading &&
      !isFetchingMoreRef.current;
    if (canLoadMore) {
      isFetchingMoreRef.current = true;
      fetchReviewsRef.current?.(page + 1, true);
    }
    prevLastIndexRef.current = lastIndex;
  }, [
    lastIndex,
    page,
    rows.length,
    hasMore,
    loading,
    fetchReviews,
    platformProp,
    platformParam,
    storeId,
    routeProductId,
  ]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Ошибка загрузки отзывов
            </h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Отзывы о товаре</h2>
          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-500">
              {reviews.length > 0 &&
                `${reviews.length}${hasMore ? "+" : ""} (всего ${
                  totalReviews ?? reviews.length
                })`}
            </div>
            <label className="flex items-center space-x-3 px-3 py-2 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border border-gray-200 shadow-sm cursor-pointer hover:bg-gray-100 transition-colors duration-200 h-10">
              <span className="text-sm font-medium text-gray-700">
                Только с текстом
              </span>
              <div className="relative inline-block w-10 h-6">
                <input
                  type="checkbox"
                  checked={onlyWithText}
                  onChange={(e) => handleOnlyWithTextChange(e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`absolute inset-0 w-10 h-6 rounded-full transition-colors duration-200 ${onlyWithText ? "bg-blue-600" : "bg-gray-300"}`}
                >
                  <div
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-200 ${onlyWithText ? "translate-x-4" : "translate-x-0"}`}
                  />
                </div>
              </div>
            </label>
          </div>
        </div>

        {loading && reviews.length === 0 ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg
                className="mx-auto h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Отзывов пока нет
            </h3>
            <p className="text-gray-500">
              Будьте первым, кто оставит отзыв об этом товаре
            </p>
          </div>
        ) : (
          <div
            className="relative"
            style={{ height: `${virtualizer.getTotalSize()}px`, width: "100%" }}
          >
            {virtualItems.map((vi) => {
              const isLoaderRow = vi.index >= rows.length;
              const review = rows[vi.index];
              return (
                <div
                  key={vi.key}
                  className="absolute left-0 right-0"
                  style={{
                    top: 0,
                    transform: `translateY(${vi.start}px)`,
                    height: `${vi.size}px`,
                  }}
                >
                  <div
                    ref={virtualizer.measureElement}
                    data-index={vi.index}
                    className={`box-border ${
                      vi.index < rows.length - 1 && vi.index < rows.length
                        ? "pb-5"
                        : "pb-0"
                    }`}
                  >
                    {isLoaderRow ? (
                      <div className="flex justify-center items-center py-6">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />
                      </div>
                    ) : (
                      <ReviewCard
                        isNew={false}
                        review={review}
                        showProductLink={false}
                        showAnalysis={true}
                        generatingResponses={generatingResponses}
                        analyzingReviews={analyzingReviews}
                        onGenerateResponse={handleGenerateResponseWrapper}
                        onRegenerateResponse={handleRegenerateResponseWrapper}
                        onAnalyzeReview={handleAnalyzeReviewWrapper}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Убрали ручную кнопку — автодогрузка при скролле вниз */}
      </div>
    </div>
  );
};

export default ProductReviews;
